/**
 * HostCard Component
 *
 * Host profile information section.
 */

import React from 'react';
import type { ListingHost } from '../hooks/useListing';

interface HostCardProps {
  host: ListingHost;
  onContact?: () => void;
}

export const HostCard: React.FC<HostCardProps> = ({ host, onContact }) => {
  const memberSince = new Date(host.createdAt).toLocaleDateString('en-US', {
    month: 'long',
    year: 'numeric',
  });

  return (
    <section className="py-6 border-b border-gray-200">
      <div className="flex items-start gap-4">
        {/* Host Avatar */}
        <div className="relative">
          {host.photoURL ? (
            <img
              src={host.photoURL}
              alt={host.displayName}
              className="w-16 h-16 rounded-full object-cover"
            />
          ) : (
            <div className="w-16 h-16 rounded-full bg-gray-200 flex items-center justify-center">
              <span className="text-2xl font-semibold text-gray-500">
                {host.displayName.charAt(0)}
              </span>
            </div>
          )}
          {host.isSuperhost && (
            <div className="absolute -bottom-1 -right-1 w-6 h-6 bg-primary-500 rounded-full flex items-center justify-center">
              <SuperhostBadge className="w-4 h-4 text-white" />
            </div>
          )}
        </div>

        {/* Host Info */}
        <div className="flex-1">
          <h2 className="text-xl font-semibold text-gray-900">
            Hosted by {host.displayName}
          </h2>
          <p className="text-sm text-gray-500">
            Joined in {memberSince}
            {host.isSuperhost && ' · Superhost'}
          </p>
        </div>
      </div>

      {/* Host Stats */}
      <div className="mt-4 flex flex-wrap gap-6 text-sm">
        {host.responseRate && (
          <div className="flex items-center gap-2">
            <ResponseIcon className="w-5 h-5 text-gray-500" />
            <span>
              <span className="font-medium">{host.responseRate}%</span> response rate
            </span>
          </div>
        )}
        {host.responseTime && (
          <div className="flex items-center gap-2">
            <ClockIcon className="w-5 h-5 text-gray-500" />
            <span>
              Responds <span className="font-medium">{host.responseTime}</span>
            </span>
          </div>
        )}
      </div>

      {/* Superhost Badge Info */}
      {host.isSuperhost && (
        <div className="mt-4 p-4 bg-gray-50 rounded-lg">
          <div className="flex items-start gap-3">
            <SuperhostBadge className="w-8 h-8 text-primary-500 shrink-0" />
            <div>
              <h3 className="font-medium text-gray-900">
                {host.displayName} is a Superhost
              </h3>
              <p className="text-sm text-gray-600 mt-1">
                Superhosts are experienced, highly rated hosts who are committed
                to providing great stays for guests.
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Contact Button */}
      <button
        onClick={onContact}
        className="mt-6 px-6 py-3 border border-gray-900 rounded-lg font-medium hover:bg-gray-50 transition-colors"
      >
        Contact host
      </button>
    </section>
  );
};

// Icons
const SuperhostBadge: React.FC<{ className?: string }> = ({ className }) => (
  <svg className={className} fill="currentColor" viewBox="0 0 24 24">
    <path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z" />
  </svg>
);

const ResponseIcon: React.FC<{ className?: string }> = ({ className }) => (
  <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
  </svg>
);

const ClockIcon: React.FC<{ className?: string }> = ({ className }) => (
  <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
  </svg>
);

export default HostCard;
